/**
 * Vue d'administration d'une lecon HTML/CSS.
 *
 * Reutilise la vue eleve pour l'apercu, mais garde la main sur le code affiche
 * et sur l'enregistrement de la configuration.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';

import { HtmlCssConfig } from '../../models/htmlcss/types';
import { HtmlCssConfigContent, HtmlCssWorkspace } from './core/types';
import { DEFAULT_CSS_CODE, DEFAULT_HTML_CODE, normalizeConfigContent } from './core/config';
import { deserializeWorkspace, serializeWorkspace } from './core/workspace';
import { getDefaultConfig } from './utils/config';

import { useCourseApi } from '../../api/courses/courses';
import HtmlCssWinConditionPanel from './HtmlCssWinConditionPanel';
import HtmlCssHelpConfigPanel from './HtmlCssHelpConfigPanel';
import HtmlCssStudent, { HtmlCssStudentRef } from './HtmlCssStudent';
import JsonEditor from '../JsonEditor/JsonEditor';
import './ResponsiveHtmlCssLayout.css';

/** Intervalle de sauvegarde automatique. */
const AUTOSAVE_INTERVAL_MS = 30_000;

interface HtmlCssAdminProps {
  lessonId: number;
  initialLesson: any;
  onSave?: (config: HtmlCssConfig) => void;
}

const HtmlCssAdmin: React.FC<HtmlCssAdminProps> = ({ lessonId, initialLesson, onSave }) => {
  const courseApi = useCourseApi();
  const studentRef = useRef<HtmlCssStudentRef>(null);

  const [editingLesson, setEditingLesson] = useState<any>(initialLesson);
  const [configContent, setConfigContent] = useState<HtmlCssConfigContent>(() =>
    normalizeConfigContent(initialLesson?.config?.config ?? initialLesson?.config)
  );
  const [workspace, setWorkspace] = useState<HtmlCssWorkspace>(() => ({
    html: DEFAULT_HTML_CODE,
    css: DEFAULT_CSS_CODE,
  }));
  const [statement, setStatement] = useState<string>(initialLesson?.statement ?? '');

  const [showWinModal, setShowWinModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    if (!initialLesson) return;

    setEditingLesson(initialLesson);
    setStatement(initialLesson.statement ?? '');

    const content = normalizeConfigContent(initialLesson.config?.config ?? initialLesson.config);
    setConfigContent(content);
    setWorkspace(
      deserializeWorkspace(content.adminWorkspace) ?? {
        html: DEFAULT_HTML_CODE,
        css: DEFAULT_CSS_CODE,
      }
    );
  }, [initialLesson]);

  /** Configuration complete, telle qu'elle est transmise aux panneaux. */
  const config = useMemo<HtmlCssConfig>(
    () => ({ lesson_id: lessonId, config: configContent }),
    [lessonId, configContent]
  );

  // Permet a une sauvegarde en vol de savoir si la configuration a change
  // depuis son envoi.
  const configRef = useRef(configContent);
  useEffect(() => {
    configRef.current = configContent;
  }, [configContent]);

  const handleSave = useCallback(
    async (options: { silent?: boolean } = {}) => {
      try {
        // Le code affiche fait foi : il peut avoir ete modifie dans l'editeur
        // sans passer par l'etat local.
        const current = studentRef.current?.getCurrentCode() ?? workspace;
        setWorkspace(current);

        // Reference de la configuration envoyee, comparee a la reception.
        const sentConfigContent = configContent;

        const lessonToSave = {
          ...editingLesson,
          statement,
          config: {
            lesson_id: lessonId,
            config: {
              ...configContent,
              adminWorkspace: serializeWorkspace(current),
            },
          },
        };

        const response = await courseApi.saveLessonDetails(lessonToSave);
        if (!response?.data?.config) {
          if (!options.silent) message.error('Aucune configuration renvoyée par le serveur.');
          return;
        }

        const savedConfig = response.data.config as HtmlCssConfig;

        // La reponse peut arriver apres que l'enseignant a modifie la
        // configuration (une modale validee pendant qu'une sauvegarde
        // automatique etait en vol). Ne reappliquer l'etat serveur que s'il
        // correspond encore a ce qui a ete envoye, sinon on ecraserait ces
        // modifications par une version anterieure.
        const stillCurrent = configRef.current === sentConfigContent;
        if (stillCurrent) {
          setConfigContent(normalizeConfigContent(savedConfig.config));
        }
        setEditingLesson((previous: any) => ({ ...previous, ...response.data }));

        if (!options.silent) message.success('Configuration enregistrée.');
        onSave?.(savedConfig);
      } catch (error: any) {
        if (!options.silent) {
          message.error(error?.response?.data ?? error?.message ?? 'Enregistrement impossible.');
        }
      }
    },
    [courseApi, editingLesson, statement, lessonId, configContent, workspace, onSave]
  );

  // La sauvegarde automatique passe par une reference pour que l'intervalle ne
  // soit pas detruit et recree a chaque frappe : il n'atteignait alors jamais
  // son echeance pendant la saisie, puis se declenchait avec une notification
  // non sollicitee.
  const autosaveRef = useRef(handleSave);
  useEffect(() => {
    autosaveRef.current = handleSave;
  }, [handleSave]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Silencieuse : une sauvegarde periodique n'a pas a interrompre le
      // travail de l'enseignant par une notification.
      autosaveRef.current({ silent: true });
    }, AUTOSAVE_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, []);

  const handleAdminAction = useCallback(
    (action: 'win' | 'help' | 'save') => {
      if (action === 'win') setShowWinModal(true);
      if (action === 'help') setShowHelpModal(true);
      if (action === 'save') handleSave();
    },
    [handleSave]
  );

  const handleImport = useCallback(
    (raw: unknown) => {
      const imported = normalizeConfigContent(
        (raw as { config?: unknown })?.config !== undefined ? (raw as any).config : raw
      );
      setConfigContent(imported);
      setWorkspace(
        deserializeWorkspace(imported.adminWorkspace) ?? {
          html: DEFAULT_HTML_CODE,
          css: DEFAULT_CSS_CODE,
        }
      );
      // `JsonEditor` confirme deja l'application : un second message ici
      // empilerait deux notifications pour une meme action.
      setImportModalOpen(false);
    },
    []
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button onClick={() => setImportModalOpen(true)}>Importer une configuration</Button>
      </div>

      <HtmlCssStudent
        ref={studentRef}
        lesson={{ ...editingLesson, config }}
        checkResult={() => {}}
        statement={statement}
        isAdmin
        onAdminAction={handleAdminAction}
        currentHtmlCode={workspace.html}
        currentCssCode={workspace.css}
        onHtmlCodeChange={html => setWorkspace(current => ({ ...current, html }))}
        onCssCodeChange={css => setWorkspace(current => ({ ...current, css }))}
        onRestoreInitial={() =>
          setWorkspace(
            deserializeWorkspace(configContent.adminWorkspace) ?? {
              html: DEFAULT_HTML_CODE,
              css: DEFAULT_CSS_CODE,
            }
          )
        }
      />

      <Modal
        open={showWinModal}
        onCancel={() => setShowWinModal(false)}
        title="Conditions de réussite"
        footer={null}
        className="htmlcss-config-modal"
        width={640}
      >
        <HtmlCssWinConditionPanel
          initialConfig={config}
          onSave={updated => {
            setConfigContent(normalizeConfigContent(updated.config));
            setShowWinModal(false);
          }}
        />
      </Modal>

      <Modal
        open={showHelpModal}
        onCancel={() => setShowHelpModal(false)}
        title="Aide proposée à l'élève"
        footer={null}
        className="htmlcss-config-modal"
        width={720}
      >
        <HtmlCssHelpConfigPanel
          initialConfig={config}
          onSave={updated => {
            setConfigContent(normalizeConfigContent(updated.config));
            setShowHelpModal(false);
          }}
        />
      </Modal>

      <Modal
        title="Importer une configuration HTML/CSS"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <JsonEditor
          title="Configuration HTML/CSS"
          value={configContent}
          sample={getDefaultConfig().config}
          showStatement
          initialStatement={statement}
          onStatementChange={setStatement}
          onApply={handleImport}
        />
      </Modal>

      <Button
        type="default"
        shape="circle"
        icon={<SaveOutlined />}
        onClick={() => handleSave()}
        aria-label="Enregistrer la configuration"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          // Reste sous les fenetres modales d'Ant Design, qui se superposaient
          // auparavant a la meme hauteur.
          zIndex: 900,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          color: '#1890ff',
        }}
      />
    </div>
  );
};

export default HtmlCssAdmin;
